import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { employeeApi } from '../axios';

export const useEmployees = (params) => useQuery({
  queryKey: ['employees', params],
  queryFn: () => employeeApi.get('/v1/employees', { params }).then(r => r.data),
  staleTime: 30000,
});

export const useEmployee = (id) => useQuery({
  queryKey: ['employees', id],
  queryFn: () => employeeApi.get(`/v1/employees/${id}`).then(r => r.data),
  enabled: !!id,
});

export const useDepartments = () => useQuery({
  queryKey: ['employees', 'departments'],
  queryFn: () => employeeApi.get('/v1/employees/departments').then(r => r.data),
  staleTime: 60000,
});

export const useDesignations = () => useQuery({
  queryKey: ['employees', 'designations'],
  queryFn: () => employeeApi.get('/v1/employees/designations').then(r => r.data),
  staleTime: 60000,
});

export const useCreateEmployee = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => employeeApi.post('/v1/employees', payload).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['employees'] }),
  });
};

export const useBulkCreateEmployees = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (employees) => employeeApi.post('/v1/employees/bulk', { employees }).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['employees'] }),
  });
};

export const useUpdateEmployee = (id) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => employeeApi.patch(`/v1/employees/${id}`, payload).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['employees'] }),
  });
};

export const useCreateDepartment = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => employeeApi.post('/v1/employees/departments', payload).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['employees', 'departments'] }),
  });
};

export const useDeleteDepartment = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => employeeApi.delete(`/v1/employees/departments/${id}`).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['employees', 'departments'] }),
  });
};

export const useCreateDesignation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => employeeApi.post('/v1/employees/designations', payload).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['employees', 'designations'] }),
  });
};

export const useDeleteDesignation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => employeeApi.delete(`/v1/employees/designations/${id}`).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['employees', 'designations'] }),
  });
};

export const useTerminateEmployee = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }) => employeeApi.post(`/v1/employees/${id}/terminate`, { reason }).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['employees'] }),
  });
};

export const useUpdateEmployeeStatus = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }) => employeeApi.patch(`/v1/employees/${id}`, { status }).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['employees'] }),
  });
};

export const useBulkUpdateEmployees = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (updates) => Promise.all(
      updates.map(({ id, payload }) => employeeApi.patch(`/v1/employees/${id}`, payload).then(r => r.data))
    ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['employees'] }),
  });
};
